import useIsSmall from "@/hooks/useIsSmall";
import { ActionIcon, Button, SegmentedControl, Stack, Text, Tooltip } from "@mantine/core";
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconBaselineDensityLarge,
  IconBaselineDensityMedium,
  IconBaselineDensitySmall,
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconUsers,
} from "@tabler/icons-react";
import moment from "moment-jalaali";
import React, { useCallback, useEffect, useState } from "react";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import DayView from "./DayView";
import MonthView from "./MonthView";
import { JalaaliCalendarProps, ViewType } from "./types";
import WeekView from "./WeekView";
import "./calendar.css";

moment.locale("fa");
moment.loadPersian();

// Height of one ten-minute slot. "spacious" is the original 30px grid.
const DENSITIES = { compact: 12, comfortable: 18, spacious: 30 } as const;
type Density = keyof typeof DENSITIES;
const DENSITY_KEY = "calendar-density";

const readDensity = (): Density => {
  try {
    const stored = localStorage.getItem(DENSITY_KEY);
    if (stored && stored in DENSITIES) return stored as Density;
  } catch { /* storage unavailable */ }
  return "comfortable";
};

const JalaaliCalendar = ({
  onEventClick,
  partners,
  setCurrentDate,
  currentDate,
  onEmptySlotClick,
  loading,
  shiftSchedule,
  view,
  setView,
  setSelectedEvent,
  members,
  stale,
}: JalaaliCalendarProps) => {
  const matches = useIsSmall();
  const locale = "fa";
  const isPersian = locale === "fa";

  const isSmall = matches;
  const isToday = !!currentDate && moment().isSame(currentDate, "day");
  const [density, setDensity] = useState<Density>(readDensity);
  const [expanded, setExpanded] = useState(false);

  const changeDensity = useCallback((value: string) => {
    setDensity(value as Density);
    try { localStorage.setItem(DENSITY_KEY, value); } catch { /* storage unavailable */ }
  }, []);

  // Focus mode: calendar covers the whole window; Escape leaves it unless a dialog is open.
  useEffect(() => {
    if (!expanded) return;
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.querySelector("[role='dialog']")) setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => { document.documentElement.style.overflow = previousOverflow; window.removeEventListener("keydown", onKey); };
  }, [expanded]);
  useEffect(() => { if (isSmall) setExpanded(false); }, [isSmall]);

  const handleViewChange = useCallback(
    (value: string) => {
      setView(value as ViewType);
      setCurrentDate(moment());
    },
    [setCurrentDate, setView]
  );

  const step = useCallback((direction: 1 | -1) => {
    setCurrentDate((prev) => {
      const newDate = prev.clone();
      switch (view) {
        case "day":
          return newDate.add(direction, "days");
        case "week":
          return newDate.add(direction * (matches ? 3 : 7), "days");
        case "month":
          // Jalali month so the displayed month always moves by exactly one.
          return newDate.add(direction, "jMonth" as moment.unitOfTime.DurationConstructor);
        default:
          return newDate;
      }
    });
  }, [view, matches, setCurrentDate]);
  const handlePrev = useCallback(() => step(-1), [step]);
  const handleNext = useCallback(() => step(1), [step]);

  const handleToday = useCallback(() => {
    setCurrentDate(moment());
  }, [setCurrentDate]);

  const openDay = useCallback((day: moment.Moment) => {
    setView("day");
    setCurrentDate(day);
  }, [setCurrentDate, setView]);

  const getTitle = () => {
    switch (view) {
      case "day":
        return isPersian
          ? currentDate?.format("jD jMMMM jYYYY")
          : currentDate?.locale("en").format("D MMMM YYYY");
      case "week": {
        const startDate = currentDate?.clone();
        const endDate = currentDate?.clone().add(matches ? 2 : 6, "days");
        return isPersian
          ? startDate?.format("jD jMMMM") + " — " + endDate?.format("jD jMMMM jYYYY")
          : startDate?.format("D MMM") + " — " + endDate?.format("D MMM YYYY");
      }
      case "month":
        return isPersian
          ? currentDate?.format("jMMMM jYYYY")
          : currentDate?.locale("en").format("MMMM YYYY");
      default:
        return "";
    }
  };

  const renderView = () => {
    switch (view) {
      case "week":
        return (
          <WeekView
            currentDate={currentDate}
            partners={partners}
            onEventClick={onEventClick}
            onEmptySlotClick={onEmptySlotClick}
            isPersian={isPersian}
            loading={false}
            shiftSchedule={shiftSchedule}
            setSelectedEvent={setSelectedEvent}
          />
        );
      case "month":
        return (
          <MonthView
            currentDate={currentDate}
            partners={partners}
            onEventClick={onEventClick}
            matches={matches}
            isPersian={isPersian}
            setSelectedEvent={setSelectedEvent}
            loading={false}
            view={view}
            onOpenDay={openDay}
          />
        );
      default:
        return (
          <DayView
            setSelectedEvent={setSelectedEvent}
            currentDate={currentDate}
            partners={partners}
            onEventClick={onEventClick}
            isPersian={isPersian}
            onEmptySlotClick={onEmptySlotClick}
            loading={false}
            shiftSchedule={shiftSchedule}
          />
        );
    }
  };

  return (
    <section
      className="calendar-workspace"
      dir="rtl"
      aria-label="تقویم نوبت‌ها"
      data-expanded={expanded || undefined}
      style={{ "--slot-h": `${DENSITIES[density]}px` } as React.CSSProperties}
    >
      <div className="calendar-toolbar">
        <div className="calendar-toolbar-group calendar-date-navigation">
          <Tooltip label="بازهٔ قبلی">
            <ActionIcon variant="default" size={36} onClick={handlePrev} aria-label="بازهٔ قبلی"><IconChevronRight size={18} /></ActionIcon>
          </Tooltip>
          <DatePicker
            className="rmdp-mobile"
            calendar={persian}
            locale={persian_fa}
            portal
            zIndex={400}
            {...(view === "day" ? { range: false, weekPicker: false, multiple: false } : {})}
            {...(view === "week" ? { range: true, weekPicker: true } : {})}
            {...(view === "month" ? { onlyMonthPicker: true, format: "MMMM YYYY" } : {})}
            value={currentDate ? currentDate.toDate() : undefined}
            onChange={(value) => {
              if (!value) return;
              if (view === "day") setCurrentDate(moment(value.valueOf()));
              else if (view === "week") {
                // Show the picked week itself (previously jumped one range ahead).
                if (Array.isArray(value) && value.length > 0) setCurrentDate(moment(value[0].valueOf()));
              } else if (view === "month") {
                const date = moment(value.valueOf());
                setCurrentDate(date.clone().startOf("jMonth").startOf("day"));
              }
            }}
            render={(_value, openCalendar) => (
              <button type="button" onClick={openCalendar} className="calendar-date-button" aria-label="انتخاب تاریخ">
                <span className="calendar-date-title">{view === "day" ? moment(currentDate).format("dddd، ") : ""}{getTitle()}</span>
                <IconCalendar size={16} stroke={1.6} />
              </button>
            )}
          />
          <Tooltip label="بازهٔ بعدی">
            <ActionIcon variant="default" size={36} onClick={handleNext} aria-label="بازهٔ بعدی"><IconChevronLeft size={18} /></ActionIcon>
          </Tooltip>
          <Button variant="light" size="compact-md" h={36} onClick={handleToday} disabled={isToday} className="calendar-today">امروز</Button>
        </div>

        <div className="calendar-toolbar-group calendar-toolbar-end">
          {!isSmall && <span className="calendar-legend"><i />خارج از شیفت</span>}
          {members && !isSmall && (
            <Button variant="default" h={36} px="sm" onClick={members.open} leftSection={<IconUsers size={16} stroke={1.6} />} aria-label="فیلتر تیم">
              تیم
              <span className="calendar-member-count">{members.selected < members.total ? `${members.selected}/${members.total}` : members.total}</span>
            </Button>
          )}
          {view !== "month" && (
            <SegmentedControl
              value={density}
              onChange={changeDensity}
              size="xs"
              className="calendar-density"
              aria-label="تراکم نمایش ساعت‌ها"
              data={[
                { value: "compact", label: <Tooltip label="فشرده"><span className="calendar-icon-option" aria-label="فشرده"><IconBaselineDensitySmall size={16} /></span></Tooltip> },
                { value: "comfortable", label: <Tooltip label="معمولی"><span className="calendar-icon-option" aria-label="معمولی"><IconBaselineDensityMedium size={16} /></span></Tooltip> },
                { value: "spacious", label: <Tooltip label="باز"><span className="calendar-icon-option" aria-label="باز"><IconBaselineDensityLarge size={16} /></span></Tooltip> },
              ]}
            />
          )}
          <SegmentedControl
            value={view}
            onChange={handleViewChange}
            size="sm"
            className="calendar-view-switch"
            aria-label="نمای تقویم"
            data={[{ label: "روزانه", value: "day" }, { label: isSmall ? "سه روز" : "هفت روز", value: "week" }, { label: "ماهانه", value: "month" }]}
          />
          {!isSmall && (
            <Tooltip label={expanded ? "خروج از تمام‌صفحه (Esc)" : "تمام‌صفحه"}>
              <ActionIcon variant="default" size={36} onClick={() => setExpanded(value => !value)} aria-label={expanded ? "خروج از تمام‌صفحه" : "تمام‌صفحه"} aria-pressed={expanded}>
                {expanded ? <IconArrowsMinimize size={18} /> : <IconArrowsMaximize size={18} />}
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="calendar-body" aria-busy={loading} data-stale={stale || undefined}>
        <div className="calendar-progress" data-active={loading || undefined} role={loading ? "status" : undefined} aria-label={loading ? "در حال به‌روزرسانی" : undefined} />
        {!loading && !partners?.length ? (
          <Stack align="center" justify="center" h="100%" mih={240} gap="xs">
            <IconCalendar size={32} stroke={1} />
            <Text fw={600}>عضوی برای نمایش وجود ندارد</Text>
            <Text size="sm" c="dimmed">فیلتر تیم را بررسی کنید.</Text>
          </Stack>
        ) : renderView()}
      </div>
    </section>
  );
};

export default JalaaliCalendar;
